// ---------------------------------------------------------------------------
// mapData — projeta o snapshot adaptado em DOIS shapes, lado a lado:
//
//   1. viewBox (SVG mock) — pinos x/y num canvas 1080x720, polyline em path-d.
//      Usado pelo HFMap quando NÃO há Google Maps (fallback / ?mock).
//   2. geo (Google Maps real, handoff 0030 §5) — pontos em lat/lng, polyline
//      codificada do Google por rota, marcador da base e flag de geometria
//      obsoleta. Usado pelo HFMap quando a Maps JS API está carregada.
//
// As bounds do viewBox são calculadas sobre TODOS os itens com coordenada
// (roteirizados + dock) pra projeção ficar estável quando um item muda de rota.
//
// Itens sem coordenada simplesmente não viram pino/marcador — continuam
// visíveis na lista do painel e no dock.
// ---------------------------------------------------------------------------

import {
   computeBounds,
   deriveBalloonEvents,
   latLngToViewBox,
   routeColorByIndex,
   shiftHHMM,
} from './derivations';

// "Iniciado" = item na mesa de operação (tem evento intermediário/terminal) OU
// terminal/done. Idle puro (sem evento) = não iniciado → pino branco com borda/
// nº na cor da rota. Espelha a regra do StopRow no painel.
function isStarted(stop) {
   return (
      stop.status === 'done' ||
      (Array.isArray(stop.timeline) && stop.timeline.length > 0)
   );
}

function coordOf(item) {
   if (!item) return null;
   const lat = Number(item.address_latitude);
   const lng = Number(item.address_longitude);
   if (
      item.address_latitude == null ||
      item.address_longitude == null ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
   ) {
      return null;
   }
   return { lat, lng };
}

// options:
//   base                → clusterOrigin adaptado (latitude/longitude do depósito)
//   staleSubclusterIds  → Set<string> de rotas com geometria obsoleta (§5.3)
export default function buildMapData(
   subclusters,
   stopsBySubcluster,
   unrouted,
   options = {}
) {
   const { base = null, staleSubclusterIds = null } = options;

   const allStops = [
      ...Object.values(stopsBySubcluster).reduce(
         (acc, arr) => acc.concat(arr),
         []
      ),
      ...unrouted,
   ];
   const bounds = computeBounds(
      allStops.map((stop) => stop.raw).filter(Boolean)
   );

   const routes = [];
   const legend = [];

   subclusters.forEach((sub, idx) => {
      const stops = stopsBySubcluster[sub.id] || [];
      if (stops.length === 0) return;

      // Cor da rota: fonte única no adapter (sub.color), sequencial pela
      // posição. Fallback por idx pro mock (que não passa pelo adapter). Rota 1
      // = 1ª cor, rota 2 = 2ª, etc.; posição estável (arrastar itens não
      // remapeia) e rotas vazias reservam o slot.
      const color = sub.color || routeColorByIndex(idx);
      legend.push({ c: color, name: sub.label });

      // ── viewBox (SVG) ──
      const pins = stops
         .map((stop) => {
            const point = stop.raw ? latLngToViewBox(stop.raw, bounds) : null;
            if (!point) return null;
            return {
               x: point.x,
               y: point.y,
               n: stop.seq,
               done: stop.status === 'done',
               run: stop.status === 'run',
               started: isStarted(stop),
               // Classe de carga (cargo_class, handoff 0032) — rótulo manual,
               // nullable. null/undefined → pino mostra cinza "N/A".
               cargo: stop.cargoClass,
            };
         })
         .filter(Boolean);

      // Polyline ligando os pinos na ordem da rota. Com <2 pinos não há linha
      // a desenhar (d vazio — HFMap pula o <path>).
      const d =
         pins.length >= 2
            ? `M ${pins[0].x} ${pins[0].y} ${pins
                 .slice(1)
                 .map((p) => `L ${p.x} ${p.y}`)
                 .join(' ')}`
            : '';

      // ── geo (Google Maps) ──
      const points = stops
         .map((stop) => {
            const coord = coordOf(stop.raw);
            if (!coord) return null;
            return {
               lat: coord.lat,
               lng: coord.lng,
               n: stop.seq,
               done: stop.status === 'done',
               run: stop.status === 'run',
               started: isStarted(stop),
               cargo: stop.cargoClass,
               eta: stop.eta,
               // Estimativa de saída = chegada + duração estimada (balão).
               etd: shiftHHMM(stop.eta, stop.dur),
               nm: stop.nm,
               id: stop.id, // order_number — tooltip (#2)
               itemId: stop.itemId, // PK — seleção/destaque (#4)
               // Marcos de evento pro balão ancorado (clique no pino).
               events: deriveBalloonEvents(stop.raw?.active_events),
            };
         })
         .filter(Boolean);

      routes.push({
         id: `r-${sub.id}`,
         subclusterId: sub.id,
         color,
         d,
         pins,
         geo: {
            // polyline codificada do Google (rota inteira). Quando ausente
            // (rota não otimizada), o HFMap liga os pontos por reta.
            polyline: sub.routeGeometry?.polyline || null,
            // geometria obsoleta: a sequência foi editada à mão após otimizar
            // (§5.3). HFMap esmaece a linha. Detecção de sessão — ver index.jsx.
            stale: staleSubclusterIds
               ? staleSubclusterIds.has(sub.id)
               : false,
            points,
         },
      });
   });

   const unroutedPins = unrouted
      .map((stop) => (stop.raw ? latLngToViewBox(stop.raw, bounds) : null))
      .filter(Boolean);

   const unroutedGeo = unrouted
      .map((stop) => {
         const coord = coordOf(stop.raw);
         if (!coord) return null;
         return {
            lat: coord.lat,
            lng: coord.lng,
            cargo: stop.cargoClass,
            nm: stop.nm,
            id: stop.id, // order_number — tooltip (#2)
            itemId: stop.itemId, // PK — seleção/destaque (#4)
         };
      })
      .filter(Boolean);

   const baseCoord =
      base && base.latitude != null && base.longitude != null
         ? { lat: Number(base.latitude), lng: Number(base.longitude) }
         : null;

   return {
      routes,
      unroutedPins,
      unroutedGeo,
      legend,
      base:
         baseCoord && !Number.isNaN(baseCoord.lat) && !Number.isNaN(baseCoord.lng)
            ? { ...baseCoord, label: base.origin_label || base.origin_code || 'Base' }
            : null,
   };
}
